import ExcelJS from "exceljs";
import { saveAs } from "file-saver";

export async function generateLiquidationExcel({
  selectedDate,
  items,
  details,
  totals,
}) {
  const workbook = new ExcelJS.Workbook();

  const titleStyle = {
    font: { bold: true, size: 14, color: { argb: "FFFFFFFF" } },
    fill: { type: "pattern", pattern: "solid", fgColor: { argb: "1F4E78" } },
    alignment: { vertical: "middle", horizontal: "left", indent: 1 },
  };

  const headerStyle = {
    font: { bold: true, color: { argb: "FFFFFFFF" } },
    fill: { type: "pattern", pattern: "solid", fgColor: { argb: "2C2E65" } },
    alignment: { horizontal: "center", vertical: "middle" },
    border: {
      top: { style: "thin" },
      left: { style: "thin" },
      bottom: { style: "thin" },
      right: { style: "thin" },
    },
  };

  const cellBorder = {
    top: { style: "thin" },
    left: { style: "thin" },
    bottom: { style: "thin" },
    right: { style: "thin" },
  };

  const moneyFormat = "R$ #,##0.00";
  const dateFormatOnly = "dd/mm/yyyy";
  const dateTimeFormat = "dd/mm/yyyy hh:mm";

  const dictionary = {
    arrangement: "Arranjo",
    brand: "Bandeira",
    type: "Tipo",
    settledCount: "Qtd Liquidado",
    settledAmount: "Valor Liquidado (R$)",
    pendingOrErrorCount: "Qtd Pendente/Erro",
    pendingOrErrorAmount: "Valor Pendente/Erro (R$)",
    totalAmount: "Total Geral (R$)",
    liquidationArrangementId: "ID Liquidação",
    merchantPaymentId: "ID do Pagamento",
    paymentScheduleDate: "Data Agendamento",
    paymentDate: "Data do Pagamento",
    sendDate: "Data de Envio",
    returnDate: "Data de Retorno",
    taxId: "CNPJ/CPF",
    establishmentId: "ID Estabelecimento",
    paymentAmount: "Valor (R$)",
    isCercPayment: "Pagamento CERC",
    channel: "Canal",
    returnMessage: "Mensagem de Retorno",
    waitingReturn: "Aguardando Retorno",
    bank: "Nome do Banco",
    agency: "Agência",
    accountNumber: "Conta",
    favoredName: "Nome do Favorecido"
  };

  const moneyFields = ["settledAmount", "pendingOrErrorAmount", "totalAmount", "paymentAmount"];
  const dateOnlyFields = ["paymentScheduleDate", "paymentDate", "returnDate"];
  const dateTimeFields = ["sendDate"];

  function translate(key) {
    return dictionary[key] || key;
  }

  function formatDateBR(dateString) {
    if (!dateString) return "";
    const [year, month, day] = dateString.split("-");
    return `${day}/${month}/${year}`;
  }

function autoFitColumns(sheet) {
    sheet.columns.forEach((column, colIndex) => {
      let maxLength = 0;
      
      column.eachCell({ includeEmpty: false }, (cell, rowNumber) => {
        if (rowNumber === 1 || rowNumber === 3) return;

        let cellValue = cell.value;
        
        if (rowNumber === 5 || (sheet.name === "Detalhamento" && rowNumber === 3)) {
          cellValue = cell.value ? cell.value.toString() : "";
        }
        else if (cellValue instanceof Date) {
          cellValue = "20/20/2026 20:20"; 
        }
        else if (typeof cellValue === "number") {
          cellValue = `R$ ${cellValue.toLocaleString("pt-BR", { minimumFractionDigits: 2 })}`;
        }

        const textLength = cellValue ? cellValue.toString().length : 0;
        if (textLength > maxLength) {
          maxLength = textLength;
        }
      });

      column.width = Math.min(Math.max(maxLength + 6, 14), 50);
    });
  }


  const sheetTotals = workbook.addWorksheet("Total por Arranjo");
  
  sheetTotals.mergeCells("A1:H1");
  const tCell1 = sheetTotals.getCell("A1");
  tCell1.value = `Resumo de Liquidação - Data: ${formatDateBR(selectedDate)}`;
  tCell1.style = titleStyle;
  sheetTotals.getRow(1).height = 30;

  sheetTotals.getCell("A3").value = `VALOR TOTAL DA LIQUIDAÇÃO:`;
  sheetTotals.getCell("A3").font = { bold: true, size: 11 };
  
  sheetTotals.getCell("D3").value = totals.totalAmount ? parseFloat(totals.totalAmount) : 0;
  sheetTotals.getCell("D3").numFmt = moneyFormat;
  sheetTotals.getCell("D3").font = { bold: true, size: 12, color: { argb: "FF1F4E78" } };

  let startRowTotals = 5;
  if (items && items.length > 0) {
    const headersTotals = Object.keys(items[0]);
    
    const headerRow = sheetTotals.getRow(startRowTotals);
    headerRow.values = headersTotals.map(h => translate(h));
    headerRow.eachCell(c => { c.style = headerStyle; });
    headerRow.height = 25;
    
    startRowTotals++;

    items.forEach(item => {
      const row = sheetTotals.getRow(startRowTotals);
      headersTotals.forEach((h, idx) => {
        const cell = row.getCell(idx + 1);
        let value = item[h];

        if (moneyFields.includes(h) && value !== null && value !== undefined) {
          value = parseFloat(value);
          cell.numFmt = moneyFormat;
        }

        cell.value = value ?? "-";
        cell.border = cellBorder;
        cell.alignment = { horizontal: "center", vertical: "middle" };
      });
      startRowTotals++;
    });
  } else {
    sheetTotals.getRow(startRowTotals).getCell(1).value = "Nenhum registro de totais encontrado.";
  }
  autoFitColumns(sheetTotals);

  const sheetDetails = workbook.addWorksheet("Detalhamento");

  sheetDetails.mergeCells("A1:Q1");
  const tCell2 = sheetDetails.getCell("A1");
  tCell2.value = `  Detalhamento das Liquidações - Data: ${formatDateBR(selectedDate)}`;
  tCell2.style = titleStyle;
  sheetDetails.getRow(1).height = 30;

  let startRowDetails = 3;
  if (details && details.length > 0) {
    const headersDetails = Object.keys(details[0]);

    const headerRow = sheetDetails.getRow(startRowDetails);
    headerRow.values = headersDetails.map(h => translate(h));
    headerRow.eachCell(c => { c.style = headerStyle; });
    headerRow.height = 25;

    startRowDetails++;

    details.forEach(item => {
      const row = sheetDetails.getRow(startRowDetails);

      headersDetails.forEach((h, idx) => {
        const cell = row.getCell(idx + 1);
        let value = item[h];

        if (dateOnlyFields.includes(h) && value) {
          value = new Date(value);
          cell.numFmt = dateFormatOnly;
        } 
        else if (dateTimeFields.includes(h) && value) {
          value = new Date(value);
          cell.numFmt = dateTimeFormat;
        }
        else if (moneyFields.includes(h) && value !== null && value !== undefined) {
          value = parseFloat(value);
          cell.numFmt = moneyFormat;
        }
        else if ((h === "isCercPayment" || h === "waitingReturn") && (value === 0 || value === 1)) {
          value = value === 1 ? "Sim" : "Não";
        }

        cell.value = value ?? "-";
        cell.border = cellBorder;
        cell.alignment = { 
          horizontal: typeof value === "number" || moneyFields.includes(h) ? "right" : "center", 
          vertical: "middle" 
        };
      });
      startRowDetails++;
    });
  } else {
    sheetDetails.getRow(startRowDetails).getCell(1).value = "Nenhum detalhe encontrado para esta data.";
  }
  autoFitColumns(sheetDetails);

  const buffer = await workbook.xlsx.writeBuffer();
  saveAs(new Blob([buffer]), `Relatorio_Liquidacao_${selectedDate}.xlsx`);
}